import { get, set } from 'idb-keyval';
import { LIMITS, type TestCase } from './protocol';

export interface Project {
	version: 1;
	source: string;
	stdin: string;
	tests: TestCase[];
	exampleId: string;
}
const key = 'c-lab-project-v1';
const isText = (value: unknown, max: number): value is string =>
	typeof value === 'string' && new TextEncoder().encode(value).length <= max;

export function isProject(value: unknown): value is Project {
	if (!value || typeof value !== 'object') return false;
	const p = value as Partial<Project>;
	return (
		p.version === 1 &&
		isText(p.source, LIMITS.sourceBytes) &&
		isText(p.stdin, LIMITS.inputBytes) &&
		typeof p.exampleId === 'string' &&
		Array.isArray(p.tests) &&
		p.tests.length <= 50 &&
		new Set(p.tests.map((t) => t?.id)).size === p.tests.length &&
		p.tests.every(
			(t) =>
				t &&
				typeof t.id === 'string' &&
				typeof t.name === 'string' &&
				isText(t.stdin, LIMITS.inputBytes) &&
				isText(t.expectedStdout, LIMITS.outputBytes) &&
				Number.isInteger(t.expectedExitCode) &&
				t.expectedExitCode >= 0 &&
				t.expectedExitCode <= 255 &&
				Number.isFinite(t.timeoutMs) &&
				t.timeoutMs >= 100 &&
				t.timeoutMs <= 30_000 &&
				typeof t.normalizeWhitespace === 'boolean',
		)
	);
}

export async function loadProject(): Promise<Project | undefined> {
	const saved: unknown = await get(key);
	if (saved === undefined) return undefined;
	if (!isProject(saved))
		throw new Error(
			'Saved project is invalid. It has not been overwritten; export your current work before reloading.',
		);
	return saved;
}

export async function saveProject(project: Project) {
	if (!isProject(project)) throw new Error('Project exceeds the supported storage limits.');
	await set(key, project);
}
